import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, BookOpen, ChevronRight } from 'lucide-react';
import api from '../api/client';
import toast from 'react-hot-toast';
import Modal from '../components/ui/Modal';
import FormField, { Input, Select, Textarea, FormRow, SubmitBtn } from '../components/ui/FormField';

const FREQ: any = { daily:'ყოველდღიური', weekly:'ყოველკვირეული', monthly:'ყოველთვიური', quarterly:'კვარტალური', yearly:'წლიური' };
const STATUS_C: any = {
  expected: { bg:'var(--accent-bg)', color:'var(--accent)', label:'მოსალოდნელი' },
  received: { bg:'var(--success-bg)', color:'var(--success)', label:'მიღებული' },
  missing:  { bg:'var(--danger-bg)', color:'var(--danger)', label:'დაკარგული' },
  late:     { bg:'var(--warning-bg)', color:'var(--warning)', label:'დაგვიანებული' },
};

export default function SerialsPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<any>(null);
  const [showNew, setShowNew] = useState(false);
  const [showNewIssue, setShowNewIssue] = useState(false);
  const [form, setForm] = useState({ title:'', issn:'', publisher:'', frequency:'monthly', notes:'' });
  const [issueForm, setIssueForm] = useState({ issue_number:'', volume:'', issue_date:'', received_date:'', status:'received', notes:'' });
  const [saving, setSaving] = useState(false);

  const { data } = useQuery({ queryKey: ['serials'], queryFn: () => api.get('/serials') } as any);
  const { data: issuesData } = useQuery({
    queryKey: ['serial-issues', selected?.id],
    queryFn: () => api.get(`/serials/${selected?.id}/issues`),
    enabled: !!selected?.id
  } as any);

  const serials = (data as any)?.data?.serials || [];
  const issues = (issuesData as any)?.data?.issues || [];
  const serial = (issuesData as any)?.data?.serial;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title) return toast.error('სათაური სავალდებულოა');
    setSaving(true);
    try {
      await api.post('/serials', form);
      toast.success('პერიოდიკა დაემატა!');
      qc.invalidateQueries({ queryKey: ['serials'] });
      setShowNew(false);
      setForm({ title:'', issn:'', publisher:'', frequency:'monthly', notes:'' });
    } catch { toast.error('შეცდომა'); }
    finally { setSaving(false); }
  };

  const handleAddIssue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!issueForm.issue_number) return toast.error('ნომერი სავალდებულოა');
    setSaving(true);
    try {
      await api.post(`/serials/${selected.id}/issues`, issueForm);
      toast.success('ნომერი დაემატა!');
      qc.invalidateQueries({ queryKey: ['serial-issues', selected.id] });
      setShowNewIssue(false);
      setIssueForm({ issue_number:'', volume:'', issue_date:'', received_date:'', status:'received', notes:'' });
    } catch { toast.error('შეცდომა'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{padding:24,fontFamily:'system-ui,sans-serif'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <div>
          <h1 style={{fontSize:20,fontWeight:600,color:'var(--text-primary)',margin:0}}>პერიოდიკა</h1>
          <p style={{fontSize:12,color:'var(--text-faint)',marginTop:4}}>ჟურნალები და სერიული გამოცემები</p>
        </div>
        <button onClick={() => setShowNew(true)}
          style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:12,fontWeight:500,cursor:'pointer'}}>
          <Plus size={15}/> ახალი პერიოდიკა
        </button>
      </div>

      <div style={{display:'grid',gridTemplateColumns:selected?'1fr 1.5fr':'1fr',gap:12}}>
        <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
          <div style={{padding:'12px 16px',borderBottom:'1px solid #1e2d45',fontSize:13,fontWeight:500,color:'var(--text-secondary)'}}>
            სერიული გამოცემები ({serials.length})
          </div>
          {serials.length===0 && <p style={{padding:32,textAlign:'center',color:'var(--text-dimmed)',fontSize:13}}>პერიოდიკა არ არის</p>}
          {serials.map((s: any) => (
            <div key={s.id}
              onClick={() => setSelected(s)}
              style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',borderBottom:'1px solid #0f1826',cursor:'pointer',
                background:selected?.id===s.id?'var(--bg-hover)':'transparent'}}
              onMouseEnter={e => (e.currentTarget.style.background='var(--bg-hover)')}
              onMouseLeave={e => (e.currentTarget.style.background=selected?.id===s.id?'var(--bg-hover)':'transparent')}>
              <div style={{width:36,height:36,borderRadius:8,background:'#1a0d2e',border:'1px solid #3a1d6e',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <BookOpen size={16} style={{color:'#a78bfa'}} />
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:500,color:'var(--text-secondary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.title}</div>
                <div style={{fontSize:11,color:'var(--text-faint)',marginTop:1}}>
                  {FREQ[s.frequency]} · {s.issn||'ISSN —'} · {s.total_issues||0} ნომერი
                </div>
              </div>
              <ChevronRight size={14} style={{color:'var(--text-dimmed)',flexShrink:0}} />
            </div>
          ))}
        </div>

        {selected && (
          <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #1e2d45',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div>
                <div style={{fontSize:13,fontWeight:500,color:'var(--text-secondary)'}}>{serial?.title||selected.title}</div>
                <div style={{fontSize:11,color:'var(--text-faint)',marginTop:1}}>{serial?.publisher||''} · {FREQ[serial?.frequency||selected.frequency]}</div>
              </div>
              <div style={{display:'flex',gap:8}}>
                <button onClick={() => setShowNewIssue(true)}
                  style={{display:'flex',alignItems:'center',gap:5,padding:'6px 12px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:12,cursor:'pointer'}}>
                  <Plus size={13}/> ნომერი
                </button>
                <button onClick={() => setSelected(null)}
                  style={{padding:'6px 10px',background:'var(--bg-dark2)',border:'1px solid #1e2d45',borderRadius:8,color:'var(--text-muted)',fontSize:12,cursor:'pointer'}}>✕</button>
              </div>
            </div>

            <div style={{maxHeight:500,overflowY:'auto'}}>
              {issues.length===0 && <p style={{padding:32,textAlign:'center',color:'var(--text-dimmed)',fontSize:13}}>ნომერი არ არის — დაამატე პირველი!</p>}
              {issues.map((issue: any) => (
                <div key={issue.id} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',borderBottom:'1px solid #0f1826'}}>
                  <div style={{flex:1}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <span style={{fontSize:13,fontWeight:500,color:'var(--text-secondary)'}}>ტ.{issue.volume||'—'} №{issue.issue_number}</span>
                      <span style={{padding:'2px 6px',borderRadius:5,fontSize:10,fontWeight:500,background:STATUS_C[issue.status]?.bg,color:STATUS_C[issue.status]?.color}}>
                        {STATUS_C[issue.status]?.label}
                      </span>
                    </div>
                    <div style={{fontSize:11,color:'var(--text-faint)',marginTop:2}}>
                      {issue.issue_date?new Date(issue.issue_date).toLocaleDateString('ka-GE'):'—'}
                      {issue.received_date ? ` · მიღება: ${new Date(issue.received_date).toLocaleDateString('ka-GE')}` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Modal open={showNew} onClose={() => setShowNew(false)} title="პერიოდიკის დამატება" width={500}>
        <form onSubmit={handleCreate}>
          <FormField label="სათაური" required>
            <Input value={form.title} onChange={e => setForm(f => ({...f,title:e.target.value}))} placeholder="ჟურნალის სახელი" />
          </FormField>
          <FormRow>
            <FormField label="ISSN">
              <Input value={form.issn} onChange={e => setForm(f => ({...f,issn:e.target.value}))} placeholder="0000-0000" />
            </FormField>
            <FormField label="სიხშირე">
              <Select value={form.frequency} onChange={e => setForm(f => ({...f,frequency:e.target.value}))}>
                <option value="daily">ყოველდღიური</option>
                <option value="weekly">ყოველკვირეული</option>
                <option value="monthly">ყოველთვიური</option>
                <option value="quarterly">კვარტალური</option>
                <option value="yearly">წლიური</option>
              </Select>
            </FormField>
          </FormRow>
          <FormField label="გამომცემელი">
            <Input value={form.publisher} onChange={e => setForm(f => ({...f,publisher:e.target.value}))} placeholder="გამომცემლობა" />
          </FormField>
          <FormField label="შენიშვნა">
            <Textarea value={form.notes} onChange={e => setForm(f => ({...f,notes:e.target.value}))} placeholder="დამატებითი ინფო..." />
          </FormField>
          <SubmitBtn loading={saving} label="დამატება" />
        </form>
      </Modal>

      <Modal open={showNewIssue} onClose={() => setShowNewIssue(false)} title="ნომრის დამატება" width={460}>
        <form onSubmit={handleAddIssue}>
          <FormRow>
            <FormField label="ნომერი" required>
              <Input value={issueForm.issue_number} onChange={e => setIssueForm(f => ({...f,issue_number:e.target.value}))} placeholder="№1" />
            </FormField>
            <FormField label="ტომი">
              <Input value={issueForm.volume} onChange={e => setIssueForm(f => ({...f,volume:e.target.value}))} placeholder="ტ.1" />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="გამოცემის თარიღი">
              <Input type="date" value={issueForm.issue_date} onChange={e => setIssueForm(f => ({...f,issue_date:e.target.value}))} />
            </FormField>
            <FormField label="მიღების თარიღი">
              <Input type="date" value={issueForm.received_date} onChange={e => setIssueForm(f => ({...f,received_date:e.target.value}))} />
            </FormField>
          </FormRow>
          <FormField label="სტატუსი">
            <Select value={issueForm.status} onChange={e => setIssueForm(f => ({...f,status:e.target.value}))}>
              <option value="received">მიღებული</option>
              <option value="expected">მოსალოდნელი</option>
              <option value="missing">დაკარგული</option>
              <option value="late">დაგვიანებული</option>
            </Select>
          </FormField>
          <SubmitBtn loading={saving} label="დამატება" />
        </form>
      </Modal>
    </div>
  );
}
